/**
 * Field Mapping tab body — Story SCH-2.1.
 *
 * Sections (epic AC3):
 *   - Confirmed mappings (sortable; AC8 drag-precedence)
 *   - High-confidence auto-matches (AC3)
 *   - Low-confidence suggestions (AC3)
 *   - No match (AC3, collapsed by default if N>20)
 *
 * Per-row controls:
 *   - Discovery rows: target selector, Confirm button, Skip button.
 *     Type-mismatch warning + "Save anyway" override (AC4).
 *   - Confirmed rows: drag handle, dormant/conflict indicators, Re-map /
 *     Disable / Re-enable / Remove buttons.
 *
 * Pending UX (AC11): every mutation calls `onMutationPendingChange(true)`
 * on start and `(false)` on resolve. The parent SubContractorHubPanel uses
 * that to no-op its onClose while any mutation is in flight.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Ban,
  Check,
  GripVertical,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';

import {
  useBulkCreateFieldMappings,
  useCreateFieldMapping,
  useDeleteFieldMapping,
  useDiscoverCatalog,
  useReorderFieldMappings,
  useUpdateFieldMapping,
} from '@/hooks/integrations/useFieldMappings';
import {
  HIGH_CONFIDENCE_MIN,
  LOW_CONFIDENCE_MIN,
  type AutoMatchResult,
  type CreateFieldMappingRequest,
  type FieldMappingRow as FieldMappingRowType,
  type JoulesChecklistCandidate,
  type SchCustomField,
  isTypeCompatible,
  isKnownSchFieldType,
} from '@/types/integrations/fieldMapping';
import { DriftBanner } from './DriftBanner';
import { JoulesTargetSelector } from './JoulesTargetSelector';

interface FieldMappingTabProps {
  /**
   * Reports per-row + bulk mutation pending state to the parent panel so
   * the panel can block its onClose while any mutation is in flight (AC11).
   */
  onMutationPendingChange?: (pending: boolean) => void;
}

const NO_MATCH_AUTO_COLLAPSE_THRESHOLD = 20;

export function FieldMappingTab({ onMutationPendingChange }: FieldMappingTabProps) {
  const { toast } = useToast();
  const discoverQuery = useDiscoverCatalog();
  const createMutation = useCreateFieldMapping();
  const bulkMutation = useBulkCreateFieldMappings();
  const updateMutation = useUpdateFieldMapping();
  const reorderMutation = useReorderFieldMappings();
  const deleteMutation = useDeleteFieldMapping();

  // Track per-row UI state: selected target id and "save anyway" override.
  const [selectedTargets, setSelectedTargets] = useState<Record<string, number | null>>({});
  const [saveAnyway, setSaveAnyway] = useState<Record<string, boolean>>({});
  const [noMatchExpanded, setNoMatchExpanded] = useState<boolean | null>(null);

  // Roll up pending state from all active mutations.
  const isAnyMutationPending =
    createMutation.isPending ||
    bulkMutation.isPending ||
    updateMutation.isPending ||
    reorderMutation.isPending ||
    deleteMutation.isPending;

  useEffect(() => {
    onMutationPendingChange?.(isAnyMutationPending);
  }, [isAnyMutationPending, onMutationPendingChange]);

  // Loading / error states.
  if (discoverQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (discoverQuery.isError || !discoverQuery.data) {
    const err = discoverQuery.error as Error & { code?: string };
    const code = err?.code;
    return (
      <Alert variant="destructive">
        <AlertTitle>
          {code === 'auth_failed'
            ? 'SubContractorHub rejected the stored API key'
            : code === 'connection_test_failed'
            ? 'Could not reach SubContractorHub'
            : code === 'integration_not_found'
            ? 'No integration found for this tenant'
            : 'Could not load catalog'}
        </AlertTitle>
        <AlertDescription>
          {err?.message ?? 'Please refresh and try again.'}
          <Button
            variant="outline"
            size="sm"
            onClick={() => discoverQuery.refetch()}
            className="mt-3"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Re-discover catalog
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  const data = discoverQuery.data;
  const candidatesById = new Map(data.joulesCandidates.map((c) => [c.id, c]));
  const confirmedKeys = new Set(data.existingMappings.map((m) => m.externalFieldKey));

  // Bucket the un-confirmed SCH fields.
  const buckets = bucketSchFields(data.schFields, data.suggestions, confirmedKeys);

  // Compute conflict map for the confirmed section: target_id → list of mapping ids.
  const targetConflictMap = computeTargetConflicts(data.existingMappings);

  // Derive default expansion of the no-match section.
  const noMatchEffectiveExpanded =
    noMatchExpanded ??
    buckets.noMatch.length <= NO_MATCH_AUTO_COLLAPSE_THRESHOLD;

  // SCH-BUG-7b (P-E2-8): empty-state when SCH returns no custom fields, so the
  // tab doesn't render an empty/confusing bucket list with no guidance.
  if (data.schFields.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            No SubContractorHub fields discovered.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => discoverQuery.refetch()}
            disabled={discoverQuery.isFetching}
          >
            {discoverQuery.isFetching ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Re-discover catalog
          </Button>
        </div>
        <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
          SubContractorHub returned no custom fields for this account. Confirm
          the connection on the Connection tab, then Re-discover.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header: re-discover + drift banner */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">
            {data.schFields.length} SubContractorHub fields ·{' '}
            {data.joulesCandidates.length} Joules checklist items available
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => discoverQuery.refetch()}
          disabled={discoverQuery.isFetching}
        >
          {discoverQuery.isFetching ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Re-discover catalog
        </Button>
      </div>

      {data.schemaDriftDetected && (
        <DriftBanner
          schFields={data.schFields}
          existingMappings={data.existingMappings}
          onRemoveMapping={(mappingId) => {
            deleteMutation.mutate(mappingId, {
              onSuccess: (result) => {
                if (result.error) {
                  toast({
                    title: 'Could not remove mapping',
                    description: result.error.message,
                    variant: 'destructive',
                  });
                } else {
                  toast({ title: 'Mapping removed' });
                }
              },
              onError: () =>
                toast({
                  title: 'Could not remove mapping',
                  description: 'Please try again.',
                  variant: 'destructive',
                }),
            });
          }}
          deletingMappingId={
            deleteMutation.isPending ? deleteMutation.variables : undefined
          }
        />
      )}

      {/* Confirmed mappings section */}
      {data.existingMappings.length > 0 && (
        <ConfirmedMappingsSection
          mappings={data.existingMappings}
          candidatesById={candidatesById}
          targetConflictMap={targetConflictMap}
          onReorder={(orderedIds) => {
            const ordering = orderedIds.map((id, index) => ({ id, precedence: index }));
            reorderMutation.mutate(
              { ordering },
              {
                onError: () => {
                  toast({
                    title: 'Could not save new order',
                    description: 'Please try again.',
                    variant: 'destructive',
                  });
                },
              },
            );
          }}
          onUpdate={(id, body) => {
            updateMutation.mutate(
              { id, body },
              {
                onError: () => {
                  toast({
                    title: 'Could not update mapping',
                    description: 'Please try again.',
                    variant: 'destructive',
                  });
                },
              },
            );
          }}
          onRemove={(id) => {
            deleteMutation.mutate(id, {
              onError: () => {
                toast({
                  title: 'Could not remove mapping',
                  description: 'Please try again.',
                  variant: 'destructive',
                });
              },
            });
          }}
          isPending={
            updateMutation.isPending ||
            reorderMutation.isPending ||
            deleteMutation.isPending
          }
        />
      )}

      {/* High-confidence auto-matches */}
      {buckets.high.length > 0 && (
        <DiscoverySection
          title="High-confidence auto-matches"
          countLabel={String(buckets.high.length)}
          variant="high"
          defaultExpanded
          headerAction={
            <Button
              size="sm"
              onClick={() => {
                const eligible = buckets.high
                  .filter(({ field, suggestion }) => {
                    if (!suggestion) return false;
                    const target = candidatesById.get(suggestion.joulesTargetId);
                    return target ? isTypeCompatible(field.fieldType, target.itemType) : false;
                  })
                  .map<CreateFieldMappingRequest>(({ field, suggestion }) => ({
                    externalFieldKey: field.fieldKey,
                    externalFieldLabel: field.fieldLabel,
                    externalFieldType: field.fieldType,
                    joulesTargetType: 'checklist_item',
                    joulesTargetId: suggestion!.joulesTargetId,
                    displayFormat: field.fieldType === 'currency' ? 'currency' : null,
                  }));
                if (eligible.length === 0) {
                  toast({
                    title: 'Nothing to confirm',
                    description:
                      'All high-confidence rows have type mismatches. Override or save anyway per row.',
                  });
                  return;
                }
                bulkMutation.mutate(
                  { rows: eligible },
                  {
                    onSuccess: (result) => {
                      if (result.error) {
                        toast({
                          title: 'Could not confirm all',
                          description:
                            result.error.code === 'mapping_conflict'
                              ? 'Some mappings already exist. Re-load and try again.'
                              : result.error.message,
                          variant: 'destructive',
                        });
                      } else {
                        toast({
                          title: `Confirmed ${result.data?.rows.length ?? eligible.length} mappings`,
                        });
                      }
                    },
                    onError: () => {
                      toast({
                        title: 'Could not confirm all',
                        description: 'Please try again.',
                        variant: 'destructive',
                      });
                    },
                  },
                );
              }}
              disabled={bulkMutation.isPending || isAnyMutationPending}
            >
              {bulkMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Confirm all
            </Button>
          }
        >
          {buckets.high.map(({ field, suggestion }) => (
            <DiscoveryFieldMappingRow
              key={field.fieldKey}
              field={field}
              suggestion={suggestion}
              candidates={data.joulesCandidates}
              candidatesById={candidatesById}
              variant="high"
              selectedTarget={
                selectedTargets[field.fieldKey] ??
                suggestion?.joulesTargetId ??
                null
              }
              onSelectTarget={(targetId) =>
                setSelectedTargets((prev) => ({ ...prev, [field.fieldKey]: targetId }))
              }
              saveAnywayChecked={!!saveAnyway[field.fieldKey]}
              onSaveAnywayChange={(checked) =>
                setSaveAnyway((prev) => ({ ...prev, [field.fieldKey]: checked }))
              }
              onConfirm={(targetId) => handleSingleConfirm(field, candidatesById, targetId)}
              onSkip={() => handleSkip(field)}
              isPending={
                createMutation.isPending &&
                createMutation.variables?.externalFieldKey === field.fieldKey
              }
            />
          ))}
        </DiscoverySection>
      )}

      {/* Low-confidence suggestions */}
      {buckets.low.length > 0 && (
        <DiscoverySection
          title="Low-confidence suggestions"
          countLabel={String(buckets.low.length)}
          variant="low"
          defaultExpanded
        >
          {buckets.low.map(({ field, suggestion }) => (
            <DiscoveryFieldMappingRow
              key={field.fieldKey}
              field={field}
              suggestion={suggestion}
              candidates={data.joulesCandidates}
              candidatesById={candidatesById}
              variant="low"
              selectedTarget={
                selectedTargets[field.fieldKey] ??
                suggestion?.joulesTargetId ??
                null
              }
              onSelectTarget={(targetId) =>
                setSelectedTargets((prev) => ({ ...prev, [field.fieldKey]: targetId }))
              }
              saveAnywayChecked={!!saveAnyway[field.fieldKey]}
              onSaveAnywayChange={(checked) =>
                setSaveAnyway((prev) => ({ ...prev, [field.fieldKey]: checked }))
              }
              onConfirm={(targetId) => handleSingleConfirm(field, candidatesById, targetId)}
              onSkip={() => handleSkip(field)}
              isPending={
                createMutation.isPending &&
                createMutation.variables?.externalFieldKey === field.fieldKey
              }
            />
          ))}
        </DiscoverySection>
      )}

      {/* No match (collapsed by default if many) */}
      {buckets.noMatch.length > 0 && (
        <DiscoverySection
          title="No match"
          countLabel={String(buckets.noMatch.length)}
          variant="none"
          defaultExpanded={noMatchEffectiveExpanded}
          onExpandChange={setNoMatchExpanded}
        >
          {buckets.noMatch.map(({ field }) => (
            <DiscoveryFieldMappingRow
              key={field.fieldKey}
              field={field}
              suggestion={null}
              candidates={data.joulesCandidates}
              candidatesById={candidatesById}
              variant="none"
              selectedTarget={selectedTargets[field.fieldKey] ?? null}
              onSelectTarget={(targetId) =>
                setSelectedTargets((prev) => ({ ...prev, [field.fieldKey]: targetId }))
              }
              saveAnywayChecked={!!saveAnyway[field.fieldKey]}
              onSaveAnywayChange={(checked) =>
                setSaveAnyway((prev) => ({ ...prev, [field.fieldKey]: checked }))
              }
              onConfirm={(targetId) => handleSingleConfirm(field, candidatesById, targetId)}
              onSkip={() => handleSkip(field)}
              isPending={
                createMutation.isPending &&
                createMutation.variables?.externalFieldKey === field.fieldKey
              }
            />
          ))}
        </DiscoverySection>
      )}
    </div>
  );

  function handleSingleConfirm(
    field: SchCustomField,
    candidatesById: Map<number, JoulesChecklistCandidate>,
    targetId: number | null,
  ) {
    if (targetId === null) {
      toast({
        title: 'Choose a target first',
        description: 'Select a Joules checklist item or click Skip.',
      });
      return;
    }
    const target = candidatesById.get(targetId);
    if (!target) {
      toast({
        title: 'Selected item is no longer available',
        description: 'Re-discover the catalog and try again.',
        variant: 'destructive',
      });
      return;
    }
    const compatible = isTypeCompatible(field.fieldType, target.itemType);
    const overridden = !!saveAnyway[field.fieldKey];
    if (!compatible && !overridden) {
      toast({
        title: 'Type mismatch',
        description:
          'Pick a compatible target or check "Save anyway" to override.',
      });
      return;
    }
    const body: CreateFieldMappingRequest = {
      externalFieldKey: field.fieldKey,
      externalFieldLabel: field.fieldLabel,
      externalFieldType: field.fieldType,
      joulesTargetType: 'checklist_item',
      joulesTargetId: targetId,
      displayFormat: field.fieldType === 'currency' ? 'currency' : null,
      notes: !compatible && overridden ? 'type_mismatch_override' : null,
    };
    createMutation.mutate(body, {
      onSuccess: (result) => {
        if (result.error) {
          toast({
            title: 'Could not save mapping',
            description: result.error.message,
            variant: 'destructive',
          });
        } else {
          toast({ title: `Mapped ${field.fieldLabel}` });
          setSelectedTargets((prev) => {
            const next = { ...prev };
            delete next[field.fieldKey];
            return next;
          });
          setSaveAnyway((prev) => {
            const next = { ...prev };
            delete next[field.fieldKey];
            return next;
          });
        }
      },
      onError: () => {
        toast({
          title: 'Could not save mapping',
          description: 'Please try again.',
          variant: 'destructive',
        });
      },
    });
  }

  function handleSkip(field: SchCustomField) {
    const body: CreateFieldMappingRequest = {
      externalFieldKey: field.fieldKey,
      externalFieldLabel: field.fieldLabel,
      externalFieldType: field.fieldType,
      joulesTargetType: 'skip',
    };
    createMutation.mutate(body, {
      onSuccess: (result) => {
        if (result.error) {
          toast({
            title: 'Could not save skip',
            description: result.error.message,
            variant: 'destructive',
          });
        } else {
          toast({ title: `Skipping ${field.fieldLabel}` });
        }
      },
      onError: () => {
        toast({
          title: 'Could not save skip',
          description: 'Please try again.',
          variant: 'destructive',
        });
      },
    });
  }
}

// ─── Bucket helper ────────────────────────────────────────────────────────
function bucketSchFields(
  schFields: SchCustomField[],
  suggestions: Record<string, AutoMatchResult | null>,
  confirmedKeys: Set<string>,
): {
  high: { field: SchCustomField; suggestion: AutoMatchResult }[];
  low: { field: SchCustomField; suggestion: AutoMatchResult }[];
  noMatch: { field: SchCustomField; suggestion: null }[];
} {
  const high: { field: SchCustomField; suggestion: AutoMatchResult }[] = [];
  const low: { field: SchCustomField; suggestion: AutoMatchResult }[] = [];
  const noMatch: { field: SchCustomField; suggestion: null }[] = [];
  for (const field of schFields) {
    if (confirmedKeys.has(field.fieldKey)) continue;
    const sug = suggestions[field.fieldKey] ?? null;
    if (sug && sug.confidence >= HIGH_CONFIDENCE_MIN) {
      high.push({ field, suggestion: sug });
    } else if (sug && sug.confidence >= LOW_CONFIDENCE_MIN) {
      low.push({ field, suggestion: sug });
    } else {
      noMatch.push({ field, suggestion: null });
    }
  }
  return { high, low, noMatch };
}

// ─── Conflict computation ─────────────────────────────────────────────────
function computeTargetConflicts(
  mappings: FieldMappingRowType[],
): Map<number, FieldMappingRowType[]> {
  const byTarget = new Map<number, FieldMappingRowType[]>();
  for (const m of mappings) {
    if (m.joulesTargetType !== 'checklist_item' || m.joulesTargetId === null) continue;
    const list = byTarget.get(m.joulesTargetId) ?? [];
    list.push(m);
    byTarget.set(m.joulesTargetId, list);
  }
  // Only keep entries with >1 mapping (those are the conflicts).
  for (const [k, v] of byTarget) {
    if (v.length < 2) byTarget.delete(k);
  }
  return byTarget;
}

// ─── DiscoverySection — collapsible wrapper ──────────────────────────────
interface DiscoverySectionProps {
  title: string;
  countLabel: string;
  variant: 'high' | 'low' | 'none';
  defaultExpanded?: boolean;
  onExpandChange?: (expanded: boolean) => void;
  headerAction?: React.ReactNode;
  children: React.ReactNode;
}

function DiscoverySection({
  title,
  countLabel,
  variant,
  defaultExpanded = true,
  onExpandChange,
  headerAction,
  children,
}: DiscoverySectionProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const variantBadge =
    variant === 'high'
      ? 'bg-green-100 text-green-800'
      : variant === 'low'
      ? 'bg-amber-100 text-amber-800'
      : 'bg-muted text-muted-foreground';
  return (
    <Card>
      <CardHeader
        className="cursor-pointer flex flex-row items-center justify-between space-y-0"
        onClick={() => {
          const next = !expanded;
          setExpanded(next);
          onExpandChange?.(next);
        }}
      >
        <div className="flex items-center gap-2">
          <CardTitle className="text-base">{title}</CardTitle>
          <Badge className={variantBadge}>{countLabel}</Badge>
        </div>
        {headerAction && <div onClick={(e) => e.stopPropagation()}>{headerAction}</div>}
      </CardHeader>
      {expanded && <CardContent className="space-y-3">{children}</CardContent>}
    </Card>
  );
}

// ─── DiscoveryFieldMappingRow — single row in any discovery section ───────
interface DiscoveryFieldMappingRowProps {
  field: SchCustomField;
  suggestion: AutoMatchResult | null;
  candidates: JoulesChecklistCandidate[];
  candidatesById: Map<number, JoulesChecklistCandidate>;
  variant: 'high' | 'low' | 'none';
  selectedTarget: number | null;
  onSelectTarget: (id: number | null) => void;
  saveAnywayChecked: boolean;
  onSaveAnywayChange: (checked: boolean) => void;
  onConfirm: (targetId: number | null) => void;
  onSkip: () => void;
  isPending: boolean;
}

function DiscoveryFieldMappingRow({
  field,
  suggestion,
  candidates,
  candidatesById,
  variant,
  selectedTarget,
  onSelectTarget,
  saveAnywayChecked,
  onSaveAnywayChange,
  onConfirm,
  onSkip,
  isPending,
}: DiscoveryFieldMappingRowProps) {
  const target = selectedTarget !== null ? candidatesById.get(selectedTarget) : undefined;
  const compatible = target ? isTypeCompatible(field.fieldType, target.itemType) : false;
  const showMismatch = target !== undefined && !compatible;
  // SCH-BUG-7b (P-E2-4): an UNRECOGNIZED SCH field type isn't a "mismatch" with
  // the chosen target — it's a type Joules can't map at all. Label it distinctly.
  const unknownType = !isKnownSchFieldType(field.fieldType);

  return (
    <div className="rounded-md border p-3 space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {variant === 'high' && <Check className="h-4 w-4 text-green-600" />}
            {variant === 'low' && <AlertTriangle className="h-4 w-4 text-amber-500" />}
            <p className="font-medium truncate">{field.fieldLabel}</p>
            <Badge variant="outline" className="text-xs">
              {field.fieldType}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">{field.fieldKey}</p>
          {suggestion && (
            <p className="text-xs text-muted-foreground mt-0.5">
              Confidence: {(suggestion.confidence * 100).toFixed(0)}%
            </p>
          )}
        </div>
      </div>

      <JoulesTargetSelector
        candidates={candidates}
        value={selectedTarget}
        onChange={onSelectTarget}
        placeholder="Choose a Joules checklist item"
        disabled={isPending}
      />

      {showMismatch && (
        <div className="space-y-1">
          <div className="flex items-start gap-2 text-xs text-amber-700">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            {unknownType ? (
              <span>
                Unrecognized SCH field type <code>{field.fieldType}</code> — Joules
                can&apos;t map it to a checklist-item type. Check the SCH field or
                save anyway to store the value as-is.
              </span>
            ) : (
              <span>
                Type mismatch: SCH <code>{field.fieldType}</code> doesn&apos;t map cleanly
                to Joules <code>{target?.itemType}</code>.
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id={`save-anyway-${field.fieldKey}`}
              checked={saveAnywayChecked}
              onCheckedChange={(checked) => onSaveAnywayChange(checked === true)}
              disabled={isPending}
            />
            <Label
              htmlFor={`save-anyway-${field.fieldKey}`}
              className="text-xs cursor-pointer"
            >
              Save anyway (data may not import correctly)
            </Label>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={() => onConfirm(selectedTarget)}
          disabled={
            isPending ||
            selectedTarget === null ||
            (showMismatch && !saveAnywayChecked)
          }
        >
          {isPending && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
          Confirm
        </Button>
        <Button size="sm" variant="ghost" onClick={onSkip} disabled={isPending}>
          Skip
        </Button>
      </div>
    </div>
  );
}

// ─── ConfirmedMappingsSection — sortable list ────────────────────────────
interface ConfirmedMappingsSectionProps {
  mappings: FieldMappingRowType[];
  candidatesById: Map<number, JoulesChecklistCandidate>;
  targetConflictMap: Map<number, FieldMappingRowType[]>;
  onReorder: (orderedIds: string[]) => void;
  onUpdate: (id: string, body: { joulesTargetId?: number | null; enabled?: boolean }) => void;
  onRemove: (id: string) => void;
  isPending: boolean;
}

function ConfirmedMappingsSection({
  mappings,
  candidatesById,
  targetConflictMap,
  onReorder,
  onUpdate,
  onRemove,
  isPending,
}: ConfirmedMappingsSectionProps) {
  // Local optimistic ordering for drag — initialized from mappings,
  // re-synced when mappings prop changes.
  const ordered = useMemo(
    () => [...mappings].sort((a, b) => a.precedence - b.precedence),
    [mappings],
  );
  const [orderedIds, setOrderedIds] = useState<string[]>(ordered.map((m) => m.id));
  useEffect(() => {
    setOrderedIds(ordered.map((m) => m.id));
  }, [ordered]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const mappingsById = new Map(mappings.map((m) => [m.id, m]));

  const handleDragEnd = (event: DragEndEvent) => {
    // SCH-BUG-7b (P-E2-9): ignore drags while a reorder is already in flight —
    // committing a second one races the first and can persist a stale order.
    if (isPending) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = orderedIds.indexOf(String(active.id));
    const newIndex = orderedIds.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(orderedIds, oldIndex, newIndex);
    setOrderedIds(next);
    onReorder(next);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div className="flex items-center gap-2">
          <CardTitle className="text-base">Confirmed mappings</CardTitle>
          <Badge variant="outline">{mappings.length}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={orderedIds} strategy={verticalListSortingStrategy}>
            {orderedIds.map((id) => {
              const row = mappingsById.get(id);
              if (!row) return null;
              const target =
                row.joulesTargetId !== null ? candidatesById.get(row.joulesTargetId) : undefined;
              const conflictPeers =
                row.joulesTargetType === 'checklist_item' && row.joulesTargetId !== null
                  ? targetConflictMap.get(row.joulesTargetId) ?? null
                  : null;
              return (
                <ConfirmedMappingRow
                  key={id}
                  row={row}
                  target={target}
                  conflictPeers={conflictPeers}
                  candidates={Array.from(candidatesById.values())}
                  onReMap={(newTargetId) =>
                    onUpdate(row.id, { joulesTargetId: newTargetId })
                  }
                  onToggleEnabled={(enabled) => onUpdate(row.id, { enabled })}
                  onRemove={() => onRemove(row.id)}
                  isPending={isPending}
                />
              );
            })}
          </SortableContext>
        </DndContext>
      </CardContent>
    </Card>
  );
}

// ─── ConfirmedMappingRow ─────────────────────────────────────────────────
interface ConfirmedMappingRowProps {
  row: FieldMappingRowType;
  target: JoulesChecklistCandidate | undefined;
  conflictPeers: FieldMappingRowType[] | null;
  candidates: JoulesChecklistCandidate[];
  onReMap: (newTargetId: number) => void;
  onToggleEnabled: (enabled: boolean) => void;
  onRemove: () => void;
  isPending: boolean;
}

function ConfirmedMappingRow({
  row,
  target,
  conflictPeers,
  candidates,
  onReMap,
  onToggleEnabled,
  onRemove,
  isPending,
}: ConfirmedMappingRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const [isReMapOpen, setIsReMapOpen] = useState(false);
  const [pendingTargetId, setPendingTargetId] = useState<number | null>(row.joulesTargetId);

  const showConflict = conflictPeers !== null && conflictPeers.length > 1;
  const winnerId = showConflict
    ? [...conflictPeers!].sort((a, b) => a.precedence - b.precedence)[0].id
    : null;
  const isWinner = showConflict && row.id === winnerId;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="rounded-md border p-3 space-y-2 bg-background"
    >
      <div className="flex items-start gap-2">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="mt-1 cursor-grab text-muted-foreground hover:text-foreground"
          aria-label="Drag to reorder"
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium truncate">{row.externalFieldLabel}</p>
            <Badge variant="outline" className="text-xs">
              {row.externalFieldType}
            </Badge>
            {!row.enabled && <Badge variant="secondary">Disabled</Badge>}
            {row.isDormant && (
              <Badge variant="destructive" className="gap-1">
                <Ban className="h-3 w-3" />
                Dormant
              </Badge>
            )}
            {row.notes === 'type_mismatch_override' && (
              <Badge variant="outline" className="border-amber-500 text-amber-700 text-xs">
                Save-anyway override
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {row.externalFieldKey}
          </p>
          <p className="text-sm mt-1">
            →{' '}
            {row.joulesTargetType === 'skip'
              ? 'Skipped (not imported)'
              : row.isDormant
              ? 'Target deleted in Joules'
              : target
              ? target.label
              : `target #${row.joulesTargetId ?? '?'}`}
          </p>
          {showConflict && (
            <div className="flex items-start gap-2 mt-1 text-xs text-amber-700">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span>
                Two SCH fields target the same Joules item.{' '}
                {isWinner
                  ? 'This mapping wins (lower precedence).'
                  : 'This mapping will be skipped during sync.'}
              </span>
            </div>
          )}
        </div>
      </div>

      {isReMapOpen && (
        <div className="space-y-2 pl-6">
          <JoulesTargetSelector
            candidates={candidates}
            value={pendingTargetId}
            onChange={setPendingTargetId}
            disabled={isPending}
          />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => {
                if (pendingTargetId === null) return;
                onReMap(pendingTargetId);
                setIsReMapOpen(false);
              }}
              disabled={isPending || pendingTargetId === null}
            >
              {isPending && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
              Save
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setIsReMapOpen(false);
                setPendingTargetId(row.joulesTargetId);
              }}
              disabled={isPending}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {!isReMapOpen && (
        <div className="flex items-center gap-2 pl-6">
          {(row.isDormant || row.joulesTargetType === 'checklist_item') && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsReMapOpen(true)}
              disabled={isPending}
            >
              Re-map
            </Button>
          )}
          {!row.isDormant &&
            (row.enabled ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onToggleEnabled(false)}
                disabled={isPending}
              >
                Disable
              </Button>
            ) : (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onToggleEnabled(true)}
                disabled={isPending}
              >
                Re-enable
              </Button>
            ))}
          <Button
            size="sm"
            variant="ghost"
            className="text-destructive hover:text-destructive"
            onClick={onRemove}
            disabled={isPending}
          >
            Remove
          </Button>
        </div>
      )}
    </div>
  );
}
